import React, { FC } from 'react';
import {
  Box,
  HStack,
  Image,
  useColorMode,
  useColorModeValue,
} from '@cardboard-ui/react';
import { graphql, useFragment } from 'utils/graphClient';
import type { LogoSessionInfo$key } from './__generated__/LogoSessionInfo.graphql';
import { useCustomTheme } from 'apps/TenantApp/theme/CustomThemeProvider/useCustomTheme';
import { useDesktopMenu } from './DesktopMenuContext';
import { useThemeEditing } from 'apps/TenantApp/screens/OrganisationSettings/screens/Themes/components/useThemeEditing';
import {
  CUSTOM_THEME_MENU_HEADER_IMAGE_VARIABLE,
  CUSTOM_THEME_MENU_HEADER_REMOVE_PADDING_VARIABLE,
  CustomThemeHeaderRemovePaddingVariable,
} from 'apps/TenantApp/theme/CustomThemeLoader';

interface LogoProps {
  sessionInfo: LogoSessionInfo$key;
}
const LogoTenant = graphql`
  fragment LogoSessionInfo on SessionInfo {
    tenant {
      name
      icon {
        url
      }
    }
  }
`;

const Logo: FC<LogoProps> = ({ sessionInfo }) => {
  const { tenant } = useFragment(LogoTenant, sessionInfo);
  const { activeCustomTheme } = useCustomTheme();
  const { getThemeVariable } = useThemeEditing();
  const menuHeaderImage = getThemeVariable(
    CUSTOM_THEME_MENU_HEADER_IMAGE_VARIABLE,
  );
  const removePaddingVariable = getThemeVariable(
    CUSTOM_THEME_MENU_HEADER_REMOVE_PADDING_VARIABLE,
  );

  const removeHeaderPadding = !!(
    JSON.parse(
      removePaddingVariable?.rawValue || '{}',
    ) as CustomThemeHeaderRemovePaddingVariable
  ).removePadding;

  const newThemeHeaderImageSrc = useColorModeValue(
    menuHeaderImage?.imageLight?.url,
    menuHeaderImage?.imageDark?.url,
  );
  const { colorMode } = useColorMode();
  const { isCollapsed } = useDesktopMenu();
  const settings =
    activeCustomTheme.desktopMenuHeader &&
    activeCustomTheme.desktopMenuHeader[colorMode];

  if (!tenant) {
    return null;
  }

  const { name, icon } = tenant;

  return (settings?.base64Image || newThemeHeaderImageSrc) && !isCollapsed ? (
    <Box p={removeHeaderPadding ? 0 : 4} pb={0}>
      <Image
        w="full"
        src={settings?.base64Image || newThemeHeaderImageSrc}
        objectFit="cover"
      />
    </Box>
  ) : (
    <Box h="6" p={4} mb={2}>
      <IconAndNameHeader iconUrl={icon?.url} name={name} />
    </Box>
  );
};

interface IconAndNameHeaderProps {
  iconUrl?: string;
  name: string;
}

const IconAndNameHeader = ({ iconUrl, name }: IconAndNameHeaderProps) => {
  const { isCollapsed } = useDesktopMenu();

  return (
    <HStack flex="1" spacing="3" justify={isCollapsed ? 'center' : 'start'}>
      {iconUrl && (
        <Image
          src={iconUrl}
          borderWidth="1px"
          borderRadius="xs"
          borderColor="white"
          borderStyle="solid"
          alt=""
          boxSize={isCollapsed ? '40px' : '25px'}
          objectFit="cover"
        />
      )}
      {!isCollapsed && (
        <Box textAlign="start">
          <Box noOfLines={1} fontWeight="semibold">
            {name}
          </Box>
        </Box>
      )}
    </HStack>
  );
};

export default Logo;
